import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Loader2, Palette, Rocket, ToggleLeft, ToggleRight, Wand2, Copy, Download, RefreshCw, Trash2, Plus, ExternalLink, RotateCcw, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// -------- Trash (Admin only) --------
export function AdminTrash() {
  const [items, setItems] = useState([]);
  const [retention, setRetention] = useState(30);
  const [loading, setLoading] = useState(true);
  const [purging, setPurging] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/trash`, { withCredentials: true });
      setItems(r.data.items || []);
      setRetention(r.data.retention_days || 30);
    } catch (e) {
      toast.error('Failed to load trash');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const restore = async (id) => {
    try {
      await axios.post(`${API}/admin/trash/${id}/restore`, {}, { withCredentials: true });
      toast.success('Restored');
      await load();
    } catch { toast.error('Failed to restore'); }
  };

  const purgeOne = async (id) => {
    if (!window.confirm('Permanently delete this project? This is irreversible.')) return;
    try {
      await axios.delete(`${API}/admin/trash/${id}`, { withCredentials: true });
      toast.success('Purged');
      await load();
    } catch { toast.error('Failed to purge'); }
  };

  const purgeAll = async () => {
    if (!window.confirm(`Permanently delete ALL ${items.length} items in trash? This is irreversible.`)) return;
    setPurging(true);
    try {
      const r = await axios.delete(`${API}/admin/trash`, { withCredentials: true });
      toast.success(`Purged ${r.data.purged} item(s)`);
      await load();
    } catch { toast.error('Failed'); }
    finally { setPurging(false); }
  };

  return (
    <div className="max-w-5xl space-y-4" data-testid="admin-trash">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-black text-white flex items-center gap-2"><Trash2 className="w-5 h-5 text-[#06B6D4]" /> Trash <span className="text-xs font-mono text-zinc-500 uppercase">Admin only</span></h3>
          <p className="text-xs text-zinc-500">Soft-deleted user projects. Auto-purged after {retention} days. Restore (rollback) or clear manually.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} className="border-[#1E1E24]" data-testid="trash-refresh">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
          <Button size="sm" onClick={purgeAll} disabled={purging || items.length === 0} className="bg-red-600 hover:bg-red-700 text-white" data-testid="trash-clear-all">
            <AlertTriangle className="w-4 h-4 mr-1" /> Clear All
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-zinc-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-[#1E1E24] rounded-xl">
          <Trash2 className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-500 text-sm">Trash is empty.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map(it => (
            <div key={it.project_id} className="bg-[#0A0A0C] border border-[#1E1E24] rounded-lg p-4 flex items-start justify-between gap-3" data-testid={`trash-item-${it.project_id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] font-mono uppercase text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded px-1.5 py-0.5">deleted</span>
                  <span className="text-[10px] font-mono text-zinc-500">{it.genre || 'game'}</span>
                  <span className="text-[10px] font-mono text-zinc-500">{it.days_left != null ? `expires in ${it.days_left}d` : ''}</span>
                </div>
                <p className="text-sm text-white font-semibold truncate">{it.name || '(untitled)'}</p>
                <p className="text-[11px] text-zinc-500 truncate">
                  by {it.author?.email || 'unknown'} • deleted {it.deleted_at?.slice(0,10)}
                </p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button size="sm" variant="ghost" onClick={() => restore(it.project_id)} className="text-emerald-400 hover:bg-emerald-500/10" title="Rollback (restore)" data-testid={`trash-restore-${it.project_id}`}>
                  <RotateCcw className="w-4 h-4 mr-1" /> Restore
                </Button>
                <Button size="sm" variant="ghost" onClick={() => purgeOne(it.project_id)} className="text-red-400 hover:bg-red-500/10" title="Clear (permanent delete)" data-testid={`trash-purge-${it.project_id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
// -------- Feature Flags --------
export function AdminFlags() {
  const [flags, setFlags] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/config/flags`).then(r => setFlags(r.data.flags)).catch(() => setFlags({}));
  }, []);

  const toggle = async (key) => {
    const next = { ...flags, [key]: !flags[key] };
    setFlags(next);
    setSaving(true);
    try {
      await axios.put(`${API}/admin/flags`, { flags: { [key]: next[key] } }, { withCredentials: true });
      toast.success(`${key} → ${next[key] ? 'ON' : 'OFF'}`);
    } catch (e) {
      toast.error('Failed to save');
      setFlags(flags);
    } finally {
      setSaving(false);
    }
  };

  if (!flags) return <div className="text-zinc-500">Loading flags…</div>;

  const groups = {
    'Public Sections': ['show_pricing', 'show_gallery', 'show_tutorial', 'show_faq', 'show_contact', 'show_featured_spotlight'],
    'Monetization & Growth': ['show_ads', 'show_custom_banner', 'payments_enabled'],
    'Authentication': ['signup_enabled', 'google_login_enabled'],
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="admin-flags">
      <div className="mb-2">
        <h3 className="text-lg font-black text-white">Site Feature Flags</h3>
        <p className="text-xs text-zinc-500">Toggle visibility of sections, monetization, and auth in real time. Changes are live immediately.</p>
      </div>
      {Object.entries(groups).map(([g, keys]) => (
        <div key={g} className="bg-[#0A0A0C] border border-[#1E1E24] rounded-xl p-5">
          <h4 className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-4">{g}</h4>
          <div className="space-y-2">
            {keys.filter(k => k in flags).map(k => (
              <button
                key={k}
                onClick={() => toggle(k)}
                disabled={saving}
                className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-white/5 transition-colors text-left"
                data-testid={`flag-toggle-${k}`}
              >
                <span className="text-sm text-white font-medium">{k}</span>
                {flags[k] ? <ToggleRight className="w-6 h-6 text-emerald-400" /> : <ToggleLeft className="w-6 h-6 text-zinc-600" />}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// -------- Site Content CMS --------
export function AdminContent() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ kind: 'banner', slot: 'landing_hero', title: '', body: '', media_url: '', link_url: '', order: 0, published: true });

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/content`, { params: { published: null } });
      setItems(r.data.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setCreating(true);
    try {
      await axios.post(`${API}/admin/content`, form, { withCredentials: true });
      toast.success('Content created');
      setForm({ kind: 'banner', slot: 'landing_hero', title: '', body: '', media_url: '', link_url: '', order: 0, published: true });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setCreating(false); }
  };

  const togglePublish = async (item) => {
    try {
      await axios.put(`${API}/admin/content/${item.content_id}`, { published: !item.published }, { withCredentials: true });
      await load();
    } catch { toast.error('Failed to update'); }
  };

  const del = async (item) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await axios.delete(`${API}/admin/content/${item.content_id}`, { withCredentials: true });
      await load();
      toast.success('Deleted');
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="max-w-4xl space-y-6" data-testid="admin-content">
      <div>
        <h3 className="text-lg font-black text-white">Site Content (CMS)</h3>
        <p className="text-xs text-zinc-500">Add banners, videos, ads or custom sections. Assign to a slot (e.g. landing_hero, gallery_top). Publish/unpublish anytime.</p>
      </div>

      <div className="bg-[#0A0A0C] border border-[#1E1E24] rounded-xl p-5">
        <h4 className="text-sm font-bold text-white mb-3 flex items-center gap-2"><Plus className="w-4 h-4" /> New content item</h4>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <select value={form.kind} onChange={(e) => setForm({...form, kind: e.target.value})} className="bg-[#1E1E24] border border-[#2E2E34] rounded px-2 py-2 text-sm text-white">
            <option value="banner">Banner</option>
            <option value="video">Video</option>
            <option value="ad">Ad</option>
            <option value="section">Section</option>
            <option value="link">Link</option>
          </select>
          <select value={form.slot} onChange={(e) => setForm({...form, slot: e.target.value})} className="bg-[#1E1E24] border border-[#2E2E34] rounded px-2 py-2 text-sm text-white">
            <option value="landing_hero">Landing Hero</option>
            <option value="landing_below_hero">Landing (below hero)</option>
            <option value="landing_footer">Landing Footer</option>
            <option value="gallery_top">Gallery Top</option>
            <option value="dashboard_top">Dashboard Top</option>
            <option value="global_alert">Global Alert</option>
          </select>
          <Input placeholder="Title" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="bg-[#1E1E24] border-[#2E2E34] text-white" />
          <Input placeholder="Link URL (optional)" value={form.link_url} onChange={(e) => setForm({...form, link_url: e.target.value})} className="bg-[#1E1E24] border-[#2E2E34] text-white" />
          <Input placeholder="Media URL (image/video)" value={form.media_url} onChange={(e) => setForm({...form, media_url: e.target.value})} className="col-span-2 bg-[#1E1E24] border-[#2E2E34] text-white" />
        </div>
        <textarea placeholder="Body / description" value={form.body} onChange={(e) => setForm({...form, body: e.target.value})} className="w-full bg-[#1E1E24] border border-[#2E2E34] rounded px-3 py-2 text-sm text-white mb-3 min-h-[80px]" />
        <div className="flex gap-2">
          <Button onClick={create} disabled={creating} className="bg-[#06B6D4] text-black hover:bg-[#06B6D4]/90" data-testid="admin-content-create">
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Create</>}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-zinc-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-zinc-500 text-sm italic">No content items yet. Create one above.</div>
      ) : (
        <div className="space-y-2">
          {items.map(it => (
            <div key={it.content_id} className="bg-[#0A0A0C] border border-[#1E1E24] rounded-lg p-4 flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] font-mono uppercase text-[#06B6D4] bg-[#06B6D4]/10 border border-[#06B6D4]/30 rounded px-1.5 py-0.5">{it.kind}</span>
                  <span className="text-[10px] font-mono text-zinc-500">{it.slot}</span>
                  {!it.published && <span className="text-[10px] font-mono text-amber-400">DRAFT</span>}
                </div>
                <p className="text-sm text-white font-semibold truncate">{it.title || '(untitled)'}</p>
                {it.body && <p className="text-xs text-zinc-400 line-clamp-2 mt-0.5">{it.body}</p>}
              </div>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="ghost" onClick={() => togglePublish(it)} className="text-zinc-400 hover:text-white">
                  {it.published ? <ToggleRight className="w-4 h-4 text-emerald-400" /> : <ToggleLeft className="w-4 h-4" />}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => del(it)} className="text-red-400"><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// -------- Promo Kit --------
export function AdminPromokit() {
  const [product, setProduct] = useState('PlayMold');
  const [tagline, setTagline] = useState('Create games with AI. No code. No limits.');
  const [audience, setAudience] = useState('indie game creators, hobbyists, students');
  const [tone, setTone] = useState('energetic, futuristic, empowering');
  const [kit, setKit] = useState(null);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);

  const loadHistory = async () => {
    try {
      const r = await axios.get(`${API}/admin/promokit`, { withCredentials: true });
      setHistory(r.data.kits || []);
      if ((r.data.kits || [])[0]) setKit(r.data.kits[0]);
    } catch {}
  };
  useEffect(() => { loadHistory(); }, []);

  const generate = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/promokit/generate`, { product_name: product, tagline, audience, tone }, { withCredentials: true });
      setKit(r.data);
      toast.success('Promo kit generated');
      loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Generation failed');
    } finally { setBusy(false); }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(typeof text === 'string' ? text : JSON.stringify(text, null, 2));
    toast.success('Copied');
  };

  const c = kit?.content || {};

  const Card = ({ title, children }) => (
    <div className="bg-[#0A0A0C] border border-[#1E1E24] rounded-xl p-5">
      <h4 className="text-sm font-bold text-white mb-3 flex items-center gap-2">{title}</h4>
      <div className="text-sm text-zinc-300 leading-relaxed space-y-2">{children}</div>
    </div>
  );

  return (
    <div className="max-w-5xl space-y-6" data-testid="admin-promokit">
      <div>
        <h3 className="text-lg font-black text-white flex items-center gap-2"><Rocket className="w-5 h-5 text-[#06B6D4]" /> Promo Kit Generator</h3>
        <p className="text-xs text-zinc-500">One-click launch pack: Google Play copy, App Store, Twitter/LinkedIn/Instagram/Reddit/ProductHunt, YouTube trailer script, email launch, press release. Powered by GPT.</p>
      </div>

      <div className="bg-[#0A0A0C] border border-[#1E1E24] rounded-xl p-5">
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Product name</label>
            <Input value={product} onChange={(e) => setProduct(e.target.value)} className="bg-[#1E1E24] border-[#2E2E34] text-white" />
          </div>
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Tagline</label>
            <Input value={tagline} onChange={(e) => setTagline(e.target.value)} className="bg-[#1E1E24] border-[#2E2E34] text-white" />
          </div>
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Target audience</label>
            <Input value={audience} onChange={(e) => setAudience(e.target.value)} className="bg-[#1E1E24] border-[#2E2E34] text-white" />
          </div>
          <div>
            <label className="text-xs text-zinc-400 mb-1 block">Tone</label>
            <Input value={tone} onChange={(e) => setTone(e.target.value)} className="bg-[#1E1E24] border-[#2E2E34] text-white" />
          </div>
        </div>
        <Button onClick={generate} disabled={busy} className="bg-[#06B6D4] text-black hover:bg-[#06B6D4]/90" data-testid="promokit-generate">
          {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating (30-60s)…</> : <><Wand2 className="w-4 h-4 mr-2" />Generate Kit</>}
        </Button>
      </div>

      {c.google_play && (
        <Card title={<><span className="text-[#06B6D4]">📱</span> Google Play <Button size="sm" variant="ghost" onClick={() => copy(c.google_play)} className="ml-auto text-zinc-500"><Copy className="w-3.5 h-3.5" /></Button></>}>
          <p><strong className="text-white">Short:</strong> {c.google_play.short_description}</p>
          <p className="whitespace-pre-wrap"><strong className="text-white">Full:</strong> {c.google_play.full_description}</p>
          <p><strong className="text-white">Keywords:</strong> {(c.google_play.keywords || []).join(', ')}</p>
          <p><strong className="text-white">Rating:</strong> {c.google_play.content_rating}</p>
          <p><strong className="text-white">Release notes:</strong> {c.google_play.release_notes}</p>
        </Card>
      )}
      {c.app_store && (
        <Card title={<><span>🍎</span> App Store <Button size="sm" variant="ghost" onClick={() => copy(c.app_store)} className="ml-auto text-zinc-500"><Copy className="w-3.5 h-3.5" /></Button></>}>
          <p><strong className="text-white">Subtitle:</strong> {c.app_store.subtitle}</p>
          <p><strong className="text-white">Promo:</strong> {c.app_store.promotional_text}</p>
          <p className="whitespace-pre-wrap">{c.app_store.description}</p>
        </Card>
      )}
      {c.youtube && (
        <Card title={<><span>📹</span> YouTube <Button size="sm" variant="ghost" onClick={() => copy(c.youtube)} className="ml-auto text-zinc-500"><Copy className="w-3.5 h-3.5" /></Button></>}>
          <p><strong className="text-white">Trailer script:</strong></p>
          <pre className="whitespace-pre-wrap bg-[#050505] p-3 rounded text-xs">{c.youtube.trailer_script}</pre>
          <p><strong className="text-white">Shorts hook:</strong> {c.youtube.shorts_hook}</p>
          <p><strong className="text-white">Titles:</strong> {(c.youtube.video_titles || []).join(' • ')}</p>
          <p className="whitespace-pre-wrap"><strong className="text-white">Description:</strong> {c.youtube.description}</p>
        </Card>
      )}
      {c.twitter && (
        <Card title={<><span>🐦</span> Twitter/X <Button size="sm" variant="ghost" onClick={() => copy(c.twitter)} className="ml-auto text-zinc-500"><Copy className="w-3.5 h-3.5" /></Button></>}>
          {c.twitter.map((t, i) => <div key={i} className="p-3 bg-[#050505] rounded"><p className="whitespace-pre-wrap">{t}</p></div>)}
        </Card>
      )}
      {c.linkedin && (
        <Card title="💼 LinkedIn">
          {c.linkedin.map((t, i) => <div key={i} className="p-3 bg-[#050505] rounded whitespace-pre-wrap">{t}</div>)}
        </Card>
      )}
      {c.instagram && (
        <Card title="📸 Instagram">
          {c.instagram.map((t, i) => <div key={i} className="p-3 bg-[#050505] rounded whitespace-pre-wrap">{t}</div>)}
        </Card>
      )}
      {c.reddit && (
        <Card title="🤖 Reddit">
          <p><strong className="text-white">Title:</strong> {c.reddit.title}</p>
          <p className="whitespace-pre-wrap">{c.reddit.body}</p>
        </Card>
      )}
      {c.producthunt && (
        <Card title="🚀 Product Hunt">
          <p><strong className="text-white">Tagline:</strong> {c.producthunt.tagline}</p>
          <p>{c.producthunt.description}</p>
          <p className="italic text-zinc-400">Founder comment: {c.producthunt.first_comment}</p>
        </Card>
      )}
      {c.email_launch && (
        <Card title="📧 Launch Email">
          <p><strong className="text-white">Subject:</strong> {c.email_launch.subject}</p>
          <p><strong className="text-white">Preheader:</strong> {c.email_launch.preheader}</p>
          <div className="p-3 bg-[#050505] rounded whitespace-pre-wrap">{c.email_launch.body}</div>
        </Card>
      )}
      {c.press_release && (
        <Card title="📰 Press Release">
          <div className="whitespace-pre-wrap">{c.press_release}</div>
        </Card>
      )}
      {c.elevator_pitch && <Card title="🎤 Elevator Pitch"><p>{c.elevator_pitch}</p></Card>}
      {c.influencer_outreach_dm && <Card title="👋 Influencer DM"><p className="whitespace-pre-wrap">{c.influencer_outreach_dm}</p></Card>}
      {c.raw && !c.google_play && <Card title="Raw output"><pre className="whitespace-pre-wrap text-xs">{c.raw}</pre></Card>}
    </div>
  );
}

// -------- Logo Kit --------
export function AdminLogokit() {
  const [manifest, setManifest] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/logokit/manifest`);
      setManifest(r.data.logos || []);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/logokit/generate`, {}, { withCredentials: true, timeout: 300000 });
      toast.success('Logo kit generated');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Generation failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-5xl space-y-6" data-testid="admin-logokit">
      <div>
        <h3 className="text-lg font-black text-white flex items-center gap-2"><Palette className="w-5 h-5 text-[#06B6D4]" /> Logo Kit</h3>
        <p className="text-xs text-zinc-500">Auto-generated by Gemini Nano Banana. 5 variants — icon, wordmark, poster, dark, light. Right-click any image to save, or use the Download button.</p>
      </div>

      <div>
        <Button onClick={generate} disabled={busy} className="bg-[#06B6D4] text-black hover:bg-[#06B6D4]/90" data-testid="logokit-generate">
          {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating (~2-3 min)…</> : <><Wand2 className="w-4 h-4 mr-2" />Generate All Variants</>}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {manifest.length === 0 && !busy && (
          <div className="col-span-3 text-zinc-500 text-sm italic">No logos yet. Click "Generate All Variants".</div>
        )}
        {manifest.map(logo => (
          <div key={logo.variant} className="bg-[#0A0A0C] border border-[#1E1E24] rounded-xl overflow-hidden">
            <div className="bg-[#050505] aspect-square flex items-center justify-center overflow-hidden">
              <img src={`${API}${logo.url.replace('/api','')}`.replace('/logokit',`${API}/logokit`)} data-testid={`logo-preview-${logo.variant}`} alt={logo.variant} className="w-full h-full object-contain" onError={(e) => { e.currentTarget.src = `${process.env.REACT_APP_BACKEND_URL}/api/logokit/${logo.variant}.png`; }} />
            </div>
            <div className="p-4 flex items-center justify-between">
              <div>
                <p className="text-sm font-bold text-white capitalize">{logo.variant}</p>
                <p className="text-[10px] font-mono text-zinc-500">{logo.mime_type}</p>
              </div>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/logokit/${logo.variant}.png`}
                download={`playmold-logo-${logo.variant}.png`}
                target="_blank"
                rel="noreferrer"
                className="text-[#06B6D4] hover:text-white p-2 rounded-lg hover:bg-white/5 transition-colors"
                data-testid={`logo-download-${logo.variant}`}
              >
                <Download className="w-4 h-4" />
              </a>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
